import { useState, useEffect } from 'react';
import { updateProfile } from '@traction/api-client';
import { useAuth } from '@/contexts/AuthContext';
import {
  classNames,
  ICP_PRESET_META,
  getIcpPresetBundle,
  type IcpPresetId,
  type Profile,
} from '@traction/shared';
import { Loader2, Check, LayoutTemplate, Wand2 } from 'lucide-react';

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'];
const COMMON_INDUSTRIES = [
  'Technology',
  'SaaS',
  'Software',
  'Healthcare',
  'Fintech',
  'E-commerce',
  'Marketing',
  'Education',
  'Publishing',
  'Media',
  'Real Estate',
  'Consulting',
  'Manufacturing',
];

const ICP_PRESET_IDS: IcpPresetId[] = ['tech_executive', 'literary_publishing', 'sales_gtm', 'custom'];

function isIcpPresetId(v: string | null | undefined): v is IcpPresetId {
  return v === 'tech_executive' || v === 'literary_publishing' || v === 'sales_gtm' || v === 'custom';
}
const COMMON_ROLES = ['CEO', 'CTO', 'CFO', 'CMO', 'VP of Sales', 'VP of Marketing', 'Head of Growth', 'Director of Engineering', 'Product Manager', 'Founder'];
const SENIORITIES = ['C-Suite', 'VP', 'Director', 'Manager', 'Senior', 'Entry'];
const FUNDING_STAGES = ['Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C', 'Series D+', 'IPO', 'Acquired'];
const REVENUE_RANGES = ['0-1M', '1M-10M', '10M-50M', '50M-100M', '100M-500M', '500M+'];
const DEPARTMENTS = ['Engineering', 'Sales', 'Marketing', 'Product', 'Operations', 'Finance', 'HR', 'Customer Success'];
const COMMON_TECHNOLOGIES = ['Salesforce', 'HubSpot', 'AWS', 'Google Cloud', 'Azure', 'Slack', 'Zoom', 'Shopify', 'React', 'Python'];

export function SettingsPage() {
  const { user, profile, refreshProfile } = useAuth();

  // Lead targeting fields
  const [targetRoles, setTargetRoles] = useState<string[]>([]);
  const [targetIndustries, setTargetIndustries] = useState<string[]>([]);
  const [targetCompanySizes, setTargetCompanySizes] = useState<string[]>([]);
  const [targetGeography, setTargetGeography] = useState<string[]>([]);
  const [targetSeniorities, setTargetSeniorities] = useState<string[]>([]);
  const [targetFundingStages, setTargetFundingStages] = useState<string[]>([]);
  const [targetKeywords, setTargetKeywords] = useState<string[]>([]);
  const [targetTechnologies, setTargetTechnologies] = useState<string[]>([]);
  const [targetRevenueRanges, setTargetRevenueRanges] = useState<string[]>([]);
  const [targetDepartments, setTargetDepartments] = useState<string[]>([]);
  const [excludeTitles, setExcludeTitles] = useState<string[]>([]);
  const [requireEmail, setRequireEmail] = useState(false);
  const [hiringIntent, setHiringIntent] = useState(false);

  // Custom inputs
  const [customRole, setCustomRole] = useState('');
  const [customIndustry, setCustomIndustry] = useState('');
  const [customLocation, setCustomLocation] = useState('');
  const [customKeyword, setCustomKeyword] = useState('');
  const [customTech, setCustomTech] = useState('');
  const [customExcludeTitle, setCustomExcludeTitle] = useState('');

  const [icpPreset, setIcpPreset] = useState<IcpPresetId>('custom');
  const [presetSelection, setPresetSelection] = useState<IcpPresetId>('custom');

  // UI state
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      const p = profile as Profile;
      setTargetRoles(p.target_roles || []);
      setTargetIndustries(p.target_industries || []);
      setTargetCompanySizes(p.target_company_sizes || []);
      setTargetGeography(p.target_geography || []);
      setTargetSeniorities(p.target_seniorities || []);
      setTargetFundingStages(p.target_funding_stages || []);
      setTargetKeywords(p.target_keywords || []);
      setTargetTechnologies(p.target_technologies || []);
      setTargetRevenueRanges(p.target_revenue_ranges || []);
      setTargetDepartments(p.target_departments || []);
      setExcludeTitles(p.exclude_titles || []);
      setRequireEmail(p.require_email ?? false);
      setHiringIntent(p.hiring_intent ?? false);
      const rawPreset = p.icp_preset;
      const normalized = isIcpPresetId(rawPreset) ? rawPreset : 'custom';
      setIcpPreset(normalized);
      setPresetSelection(normalized);
    }
  }, [profile]);

  function toggleItem(array: string[], item: string, setter: (arr: string[]) => void) {
    if (array.includes(item)) {
      setter(array.filter(i => i !== item));
    } else {
      setter([...array, item]);
    }
  }

  function addCustomItem(value: string, array: string[], setter: (arr: string[]) => void, clearInput: () => void) {
    const trimmed = value.trim();
    if (trimmed && !array.includes(trimmed)) {
      setter([...array, trimmed]);
      clearInput();
    }
  }

  function mergeUnique(prev: string[], next: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of [...prev, ...next]) {
      const k = (v || '').trim();
      if (!k) continue;
      const kk = k.toLowerCase();
      if (!seen.has(kk)) {
        seen.add(kk);
        out.push(k);
      }
    }
    return out;
  }

  /** Apply preset chip defaults (does not change geography, exclude titles, or options). */
  function applyIcpPreset(id: IcpPresetId, mode: 'replace' | 'merge') {
    setIcpPreset(id);
    const bundle = getIcpPresetBundle(id);
    if (!bundle) return;

    setTargetRoles((prev) => (mode === 'merge' ? mergeUnique(prev, bundle.target_roles) : [...bundle.target_roles]));
    setTargetIndustries((prev) =>
      mode === 'merge' ? mergeUnique(prev, bundle.target_industries) : [...bundle.target_industries]
    );
    setTargetCompanySizes((prev) =>
      mode === 'merge' ? mergeUnique(prev, bundle.target_company_sizes) : [...bundle.target_company_sizes]
    );
    setTargetSeniorities((prev) =>
      mode === 'merge' ? mergeUnique(prev, bundle.target_seniorities) : [...bundle.target_seniorities]
    );
    setTargetFundingStages((prev) =>
      mode === 'merge' ? mergeUnique(prev, bundle.target_funding_stages) : [...bundle.target_funding_stages]
    );
    setTargetKeywords((prev) => (mode === 'merge' ? mergeUnique(prev, bundle.target_keywords) : [...bundle.target_keywords]));
    setTargetTechnologies((prev) =>
      mode === 'merge' ? mergeUnique(prev, bundle.target_technologies) : [...bundle.target_technologies]
    );
    setTargetRevenueRanges((prev) =>
      mode === 'merge' ? mergeUnique(prev, bundle.target_revenue_ranges) : [...bundle.target_revenue_ranges]
    );
    setTargetDepartments((prev) =>
      mode === 'merge' ? mergeUnique(prev, bundle.target_departments) : [...bundle.target_departments]
    );
  }

  async function handleSave() {
    if (!user) return;

    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      await updateProfile(user.uid, {
        target_roles: targetRoles,
        target_industries: targetIndustries,
        target_company_sizes: targetCompanySizes,
        target_geography: targetGeography,
        target_seniorities: targetSeniorities,
        target_funding_stages: targetFundingStages,
        target_keywords: targetKeywords,
        target_technologies: targetTechnologies,
        target_revenue_ranges: targetRevenueRanges,
        target_departments: targetDepartments,
        exclude_titles: excludeTitles,
        require_email: requireEmail,
        hiring_intent: hiringIntent,
        icp_preset: icpPreset,
      });
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl">
      <h1 className="font-heading text-lg font-medium text-[#F5F5F5]">Settings</h1>
      <p className="mt-1 text-sm text-cacao-muted">Tell us who to find. The more specific, the better the leads.</p>

      <div className="mt-6 space-y-5">
        {/* Preset: user persona → default chips */}
        <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary/40 p-4">
          <div className="flex items-start gap-3">
            <LayoutTemplate className="h-4 w-4 shrink-0 text-cacao-gold mt-0.5" />
            <div className="min-w-0 flex-1 space-y-2">
              <div>
                <label htmlFor="icp-preset" className="block text-xs font-medium text-cacao-muted">
                  Preset
                </label>
                <p className="mt-0.5 text-[10px] text-cacao-text-tertiary">
                  Load a template for different outbound personas (e.g. tech exec vs literary/publishing). Applying a
                  preset updates job titles, industries, seniority, and related chips. Location, exclude titles, and the
                  checkboxes below are left as-is.
                </p>
              </div>
              <select
                id="icp-preset"
                value={presetSelection}
                onChange={(e) => setPresetSelection(e.target.value as IcpPresetId)}
                className="w-full max-w-md rounded-md border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-xs text-[#F5F5F5] focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
              >
                {ICP_PRESET_IDS.map((id) => (
                  <option key={id} value={id}>
                    {ICP_PRESET_META[id].label}
                  </option>
                ))}
              </select>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-[10px] leading-snug text-cacao-muted">{ICP_PRESET_META[presetSelection].description}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={presetSelection === 'custom'}
                    onClick={() => applyIcpPreset(presetSelection, 'replace')}
                    className="inline-flex items-center gap-1.5 rounded-md border border-cacao-dark-tertiary px-2.5 py-1.5 text-[11px] font-medium text-cacao-muted hover:border-cacao-gold hover:text-white disabled:opacity-40 disabled:hover:border-cacao-dark-tertiary disabled:hover:text-cacao-muted transition-colors"
                  >
                    <Wand2 className="h-3 w-3" />
                    Replace
                  </button>
                  <button
                    type="button"
                    disabled={presetSelection === 'custom'}
                    onClick={() => applyIcpPreset(presetSelection, 'merge')}
                    className="inline-flex items-center gap-1.5 rounded-md border border-cacao-dark-tertiary px-2.5 py-1.5 text-[11px] font-medium text-cacao-muted hover:border-cacao-gold hover:text-white disabled:opacity-40 disabled:hover:border-cacao-dark-tertiary disabled:hover:text-cacao-muted transition-colors"
                  >
                    <Wand2 className="h-3 w-3" />
                    Merge
                  </button>
                  <button
                    type="button"
                    disabled={presetSelection === 'custom'}
                    onClick={async () => {
                      applyIcpPreset(presetSelection, 'replace');
                      await handleSave();
                    }}
                    className="inline-flex items-center gap-1.5 rounded-md bg-cacao-gold px-2.5 py-1.5 text-[11px] font-semibold text-cacao-dark hover:bg-cacao-gold-hover disabled:opacity-40 transition-colors"
                  >
                    Apply + Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Target Roles */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-2">Job Titles</label>
          <p className="text-[10px] text-cacao-text-tertiary mb-2">Multiple titles match any (OR).</p>
          <div className="flex flex-wrap gap-2 mb-2">
            {COMMON_ROLES.map(role => (
              <button
                key={role}
                onClick={() => toggleItem(targetRoles, role, setTargetRoles)}
                className={classNames(
                  'rounded-full px-2.5 py-1 text-xs transition-colors',
                  targetRoles.includes(role)
                    ? 'bg-cacao-gold text-cacao-dark'
                    : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold hover:text-white'
                )}
              >
                {role}
              </button>
            ))}
          </div>
          {targetRoles.filter(r => !COMMON_ROLES.includes(r)).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {targetRoles.filter(r => !COMMON_ROLES.includes(r)).map(role => (
                <span key={role} className="inline-flex items-center gap-1 rounded-full bg-cacao-gold px-2.5 py-1 text-xs text-cacao-dark">
                  {role}
                  <button onClick={() => toggleItem(targetRoles, role, setTargetRoles)} className="hover:text-red-600">&times;</button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={customRole}
              onChange={(e) => setCustomRole(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCustomItem(customRole, targetRoles, setTargetRoles, () => setCustomRole(''))}
              placeholder="Add custom title..."
              className="flex-1 rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-xs text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
            />
            <button
              onClick={() => addCustomItem(customRole, targetRoles, setTargetRoles, () => setCustomRole(''))}
              className="rounded-md border border-cacao-dark-tertiary px-3 py-1.5 text-xs text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Seniority */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-2">Seniority Level</label>
          <div className="flex flex-wrap gap-2">
            {SENIORITIES.map(seniority => (
              <button
                key={seniority}
                onClick={() => toggleItem(targetSeniorities, seniority, setTargetSeniorities)}
                className={classNames(
                  'rounded-full px-2.5 py-1 text-xs transition-colors',
                  targetSeniorities.includes(seniority)
                    ? 'bg-cacao-gold text-cacao-dark'
                    : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold hover:text-white'
                )}
              >
                {seniority}
              </button>
            ))}
          </div>
        </div>

        {/* Industries */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-2">Industries</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {COMMON_INDUSTRIES.map(industry => (
              <button
                key={industry}
                onClick={() => toggleItem(targetIndustries, industry, setTargetIndustries)}
                className={classNames(
                  'rounded-full px-2.5 py-1 text-xs transition-colors',
                  targetIndustries.includes(industry)
                    ? 'bg-cacao-gold text-cacao-dark'
                    : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold hover:text-white'
                )}
              >
                {industry}
              </button>
            ))}
          </div>
          {targetIndustries.filter(i => !COMMON_INDUSTRIES.includes(i)).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {targetIndustries.filter(i => !COMMON_INDUSTRIES.includes(i)).map(industry => (
                <span key={industry} className="inline-flex items-center gap-1 rounded-full bg-cacao-gold px-2.5 py-1 text-xs text-cacao-dark">
                  {industry}
                  <button onClick={() => toggleItem(targetIndustries, industry, setTargetIndustries)} className="hover:text-red-600">&times;</button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={customIndustry}
              onChange={(e) => setCustomIndustry(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCustomItem(customIndustry, targetIndustries, setTargetIndustries, () => setCustomIndustry(''))}
              placeholder="Add custom industry..."
              className="flex-1 rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-xs text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
            />
            <button
              onClick={() => addCustomItem(customIndustry, targetIndustries, setTargetIndustries, () => setCustomIndustry(''))}
              className="rounded-md border border-cacao-dark-tertiary px-3 py-1.5 text-xs text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Company Size */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-2">Company Size (employees)</label>
          <div className="flex flex-wrap gap-2">
            {COMPANY_SIZES.map(size => (
              <button
                key={size}
                onClick={() => toggleItem(targetCompanySizes, size, setTargetCompanySizes)}
                className={classNames(
                  'rounded-full px-2.5 py-1 text-xs transition-colors',
                  targetCompanySizes.includes(size)
                    ? 'bg-cacao-gold text-cacao-dark'
                    : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold hover:text-white'
                )}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        {/* Location */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-2">Location</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {targetGeography.map(location => (
              <span key={location} className="inline-flex items-center gap-1 rounded-full bg-cacao-gold px-2.5 py-1 text-xs text-cacao-dark">
                {location}
                <button onClick={() => toggleItem(targetGeography, location, setTargetGeography)} className="hover:text-red-600">&times;</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={customLocation}
              onChange={(e) => setCustomLocation(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCustomItem(customLocation, targetGeography, setTargetGeography, () => setCustomLocation(''))}
              placeholder="e.g., United States, California, New York..."
              className="flex-1 rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-xs text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
            />
            <button
              onClick={() => addCustomItem(customLocation, targetGeography, setTargetGeography, () => setCustomLocation(''))}
              className="rounded-md border border-cacao-dark-tertiary px-3 py-1.5 text-xs text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Funding Stage */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-2">Funding Stage</label>
          <div className="flex flex-wrap gap-2">
            {FUNDING_STAGES.map(stage => (
              <button
                key={stage}
                onClick={() => toggleItem(targetFundingStages, stage, setTargetFundingStages)}
                className={classNames(
                  'rounded-full px-2.5 py-1 text-xs transition-colors',
                  targetFundingStages.includes(stage)
                    ? 'bg-cacao-gold text-cacao-dark'
                    : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold hover:text-white'
                )}
              >
                {stage}
              </button>
            ))}
          </div>
        </div>

        {/* Revenue */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-2">Company Revenue</label>
          <div className="flex flex-wrap gap-2">
            {REVENUE_RANGES.map(range => (
              <button
                key={range}
                onClick={() => toggleItem(targetRevenueRanges, range, setTargetRevenueRanges)}
                className={classNames(
                  'rounded-full px-2.5 py-1 text-xs transition-colors',
                  targetRevenueRanges.includes(range)
                    ? 'bg-cacao-gold text-cacao-dark'
                    : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold hover:text-white'
                )}
              >
                ${range}
              </button>
            ))}
          </div>
        </div>

        {/* Departments */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-2">Departments</label>
          <div className="flex flex-wrap gap-2">
            {DEPARTMENTS.map(dept => (
              <button
                key={dept}
                onClick={() => toggleItem(targetDepartments, dept, setTargetDepartments)}
                className={classNames(
                  'rounded-full px-2.5 py-1 text-xs transition-colors',
                  targetDepartments.includes(dept)
                    ? 'bg-cacao-gold text-cacao-dark'
                    : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold hover:text-white'
                )}
              >
                {dept}
              </button>
            ))}
          </div>
        </div>

        {/* Technologies */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-2">Technologies They Use</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {COMMON_TECHNOLOGIES.map(tech => (
              <button
                key={tech}
                onClick={() => toggleItem(targetTechnologies, tech, setTargetTechnologies)}
                className={classNames(
                  'rounded-full px-2.5 py-1 text-xs transition-colors',
                  targetTechnologies.includes(tech)
                    ? 'bg-cacao-gold text-cacao-dark'
                    : 'border border-cacao-dark-tertiary text-cacao-muted hover:border-cacao-gold hover:text-white'
                )}
              >
                {tech}
              </button>
            ))}
          </div>
          {targetTechnologies.filter(t => !COMMON_TECHNOLOGIES.includes(t)).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {targetTechnologies.filter(t => !COMMON_TECHNOLOGIES.includes(t)).map(tech => (
                <span key={tech} className="inline-flex items-center gap-1 rounded-full bg-cacao-gold px-2.5 py-1 text-xs text-cacao-dark">
                  {tech}
                  <button onClick={() => toggleItem(targetTechnologies, tech, setTargetTechnologies)} className="hover:text-red-600">&times;</button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={customTech}
              onChange={(e) => setCustomTech(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCustomItem(customTech, targetTechnologies, setTargetTechnologies, () => setCustomTech(''))}
              placeholder="Add technology..."
              className="flex-1 rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-xs text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
            />
            <button
              onClick={() => addCustomItem(customTech, targetTechnologies, setTargetTechnologies, () => setCustomTech(''))}
              className="rounded-md border border-cacao-dark-tertiary px-3 py-1.5 text-xs text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Keywords */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-2">Keywords</label>
          <p className="text-xs text-cacao-text-tertiary mb-2">Find companies mentioning these terms</p>
          <div className="flex flex-wrap gap-2 mb-2">
            {targetKeywords.map(keyword => (
              <span key={keyword} className="inline-flex items-center gap-1 rounded-full bg-cacao-gold px-2.5 py-1 text-xs text-cacao-dark">
                {keyword}
                <button onClick={() => toggleItem(targetKeywords, keyword, setTargetKeywords)} className="hover:text-red-600">&times;</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={customKeyword}
              onChange={(e) => setCustomKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCustomItem(customKeyword, targetKeywords, setTargetKeywords, () => setCustomKeyword(''))}
              placeholder="Add keyword..."
              className="flex-1 rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-xs text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
            />
            <button
              onClick={() => addCustomItem(customKeyword, targetKeywords, setTargetKeywords, () => setCustomKeyword(''))}
              className="rounded-md border border-cacao-dark-tertiary px-3 py-1.5 text-xs text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Exclude Titles */}
        <div>
          <label className="block text-xs font-medium text-cacao-muted mb-2">Exclude These Titles</label>
          <p className="text-xs text-cacao-text-tertiary mb-2">Skip leads with these job titles</p>
          <div className="flex flex-wrap gap-2 mb-2">
            {excludeTitles.map(title => (
              <span key={title} className="inline-flex items-center gap-1 rounded-full bg-red-500/20 border border-red-500/40 px-2.5 py-1 text-xs text-red-400">
                {title}
                <button onClick={() => toggleItem(excludeTitles, title, setExcludeTitles)} className="hover:text-white">&times;</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={customExcludeTitle}
              onChange={(e) => setCustomExcludeTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCustomItem(customExcludeTitle, excludeTitles, setExcludeTitles, () => setCustomExcludeTitle(''))}
              placeholder="e.g., Intern, Student..."
              className="flex-1 rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-1.5 text-xs text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none"
            />
            <button
              onClick={() => addCustomItem(customExcludeTitle, excludeTitles, setExcludeTitles, () => setCustomExcludeTitle(''))}
              className="rounded-md border border-cacao-dark-tertiary px-3 py-1.5 text-xs text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Options */}
        <div className="space-y-3 pt-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={requireEmail}
              onChange={(e) => setRequireEmail(e.target.checked)}
              className="h-4 w-4 rounded border-cacao-dark-tertiary bg-cacao-dark-tertiary text-cacao-gold focus:ring-cacao-gold"
            />
            <div>
              <span className="text-xs text-[#F5F5F5]">Only verified emails</span>
              <p className="text-xs text-cacao-text-tertiary">Skip leads without valid email addresses</p>
            </div>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={hiringIntent}
              onChange={(e) => setHiringIntent(e.target.checked)}
              className="h-4 w-4 rounded border-cacao-dark-tertiary bg-cacao-dark-tertiary text-cacao-gold focus:ring-cacao-gold"
            />
            <div>
              <span className="text-xs text-[#F5F5F5]">Companies that are hiring</span>
              <p className="text-xs text-cacao-text-tertiary">Focus on growing companies with job openings</p>
            </div>
          </label>
        </div>

        {/* Save Button */}
        {error && (
          <p className="text-xs text-red-400">{error}</p>
        )}

        <div className="pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-cacao-gold px-4 py-2.5 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <Check className="h-4 w-4" />
                Saved!
              </>
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
